import React from 'react'

/**
 * KeyStats — a structured statistic block engineered specifically for
 * AI Overview / AI Mode citation extraction.
 *
 * Why it exists (June 2026 research):
 *
 * Pages above ~20,000 characters average ~10.18 AI Overview citations
 * vs ~2.39 for short pages. But length alone isn't the signal — the
 * citation extractors specifically look for:
 *   - Clearly structured atomic facts (name → value → source)
 *   - Each fact paired with a verifiable primary citation
 *   - Semantic HTML (<dl>, <dt>, <dd>) that lets the parser tokenise
 *     name/value pairs without natural-language inference
 *   - Front-loaded placement (top ~40% of the article wins ~44% of
 *     all extracted citations)
 *
 * This component renders exactly that: a name-value list of headline
 * statistics with primary-source attribution. It's the single highest
 * leverage block we can add to a flagship post for AIO citation rate.
 *
 * Visually: a tight grid that reads like a fact-card the reader scans
 * in 5 seconds. Functionally: semantic <dl> + <dt> + <dd> with
 * Claim schema in JSON-LD so AI engines can extract each fact as a
 * structured triple.
 *
 * Use at the TOP of a flagship article, after BlogTLDR but before the
 * first H2. One block per post — don't fragment.
 */
export interface KeyStat {
  /** The number / threshold itself, eg "240,000". Becomes the headline of the card. */
  value: string
  /** Unit / qualifier, eg "particles per liter". Renders below the value. */
  label: string
  /** Free-text source attribution shown to the reader, eg "PNAS 2024". */
  source: string
  /**
   * Full citation URL — clickable. Omit only when the source is our own
   * first-party work (e.g. an in-house INCI audit), which has no external URL
   * to point at; the attribution then renders as plain text rather than a link.
   */
  sourceUrl?: string
  /** Optional context line, eg "tested across 25 bottled water brands". */
  context?: string
}

export interface KeyStatsProps {
  /** Optional heading above the grid. Default: "By the numbers". */
  heading?: string
  /** 2 to 6 stats. Anything outside that range degrades the visual + citation density. */
  stats: KeyStat[]
}

export default function KeyStats({ heading = 'By the numbers', stats }: KeyStatsProps) {
  // JSON-LD ClaimReview-ish structure expressing each stat as a structured
  // Claim with a citation. Uses schema.org/Claim — actively read by AI
  // engine extractors and indexed by Google AIO as of late 2025 / early 2026.
  // Each Claim has an `appearance` (the URL of the source paper) and a
  // textual `text` field with the asserted value + label.
  const claimsJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: heading,
    itemListElement: stats.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Claim',
        text: `${s.value} ${s.label}`,
        ...(s.context ? { description: s.context } : {}),
        appearance: {
          '@type': 'CreativeWork',
          name: s.source,
          ...(s.sourceUrl ? { url: s.sourceUrl } : {}),
        },
      },
    })),
  }

  return (
    <section
      aria-label={heading}
      className="not-prose my-8 rounded-2xl border border-[#1E6B47]/30 bg-gradient-to-br from-[#1E6B47]/10 via-white to-white p-5 md:p-6 shadow-sm"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(claimsJsonLd) }}
      />

      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1E6B47] mb-4">
        {heading}
      </p>

      {/* items-stretch (grid default) makes every cell in a row share the
          tallest cell's height; each <dd> is then a full-height flex column
          with the source link pushed to the bottom via mt-auto, so the orange
          citation links align on a single baseline across the row regardless
          of how much label/context text sits above them. */}
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-5">
        {stats.map((stat, i) => (
          // The label is the <dt> and is VISIBLE. It used to be rendered
          // twice — once as <dt className="sr-only"> and again as a visible
          // <span> inside the <dd> — which made screen readers announce every
          // label twice and made copied/extracted text read
          // "6 Apr 2026 / draft CCL 6 released / ... / draft CCL 6 released".
          // That duplication defeated the whole point of the component, which
          // is clean atomic name→value extraction for AI citation.
          //
          // Visual order stays value-then-label via flex `order`, so the
          // rendering is unchanged while the DOM keeps dt before dd. One <dt>
          // with several <dd> children is valid inside a <dl> div wrapper.
          <div key={i} className="min-w-0 h-full flex flex-col">
            <dt className="order-2 block text-sm font-medium text-gray-700 leading-snug">
              {stat.label}
            </dt>
            <dd className="order-1 block text-2xl md:text-3xl font-bold text-gray-900 leading-none mb-1 tabular-nums break-words">
              {stat.value}
            </dd>
            {stat.context && (
              <dd className="order-3 block text-xs text-gray-500 leading-snug mt-1">
                {stat.context}
              </dd>
            )}
              {/* Source "shelf": mt-auto pins the block to the bottom of the
                  cell; the hairline top border + pt-3 gap give every citation a
                  shared baseline across the row (KPI-card layered structure,
                  value → label → context → source). 8pt-scale spacing. */}
            <dd className="order-4 mt-auto pt-3">
              {stat.sourceUrl ? (
                <a
                  href={stat.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block border-t border-[#1E6B47]/20 pt-2 text-2xs font-semibold text-[#1E6B47] hover:text-[#155235] underline decoration-[#1E6B47]/40 underline-offset-2"
                >
                  {stat.source}
                </a>
              ) : (
                <span className="block border-t border-[#1E6B47]/20 pt-2 text-2xs font-semibold text-[#1E6B47]">
                  {stat.source}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
